// University of San Diego: private college page (#2839), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs.
//  - USD's Common Data Set 2025-2026 (cds_2025-26.pdf, the newest edition on
//    the Facts page, sandiego.edu/facts/cds): C1 applicants, admits and
//    enrollees for Fall 2025 with the residency table (its Unknown column is
//    0 applied and 0 admitted, so it is left out), C8A (USD does not use SAT
//    or ACT scores in admission decisions), C21/C22 (early decision and
//    nonbinding early action, both closing November 1, no counts), G1/G5
//    2026-2027 costs. The PDF's text layer is partly garbled, so C1, C8, C21,
//    C22 and G1 were read from the rendered pages.
//  - USD Undergraduate Admission: Admission Timeline (fall deadlines,
//    decision release and enrollment confirmation dates), Frequently Asked
//    Questions (Early Decision terms), Application Requirements ("New
//    application deadlines are now in effect starting for the Fall 2027
//    entering class") and Test-Free Information. The Apply page lists the
//    same four fall 2027 dates with years.
// Departures: the CDS gives December 15 as the early decision notification
// date; the Admission Timeline says Early Decision I decisions come in early
// December, and the page uses the timeline. The Apply page also shows Fall
// 2026 figures (about 22,600 applications, 38% admitted); they are rounded
// and not in a CDS yet, so the counts here stay the Fall 2025 CDS ones.
// Cost: the CDS G1/G5 items for a resident student are used. They match the
// Torero Hub's 2026-27 Undergraduate Educational Cost page (tuition $64,100,
// fees $1,099, housing $13,450, meal plan $5,750; books $1,320,
// transportation $1,386, personal $3,969), which also lists a $4,158 student
// health insurance premium that the CDS budget does not include.
//  - College Scorecard API, school id 122436 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'USD Common Data Set 2025-2026',
        url: 'https://www.sandiego.edu/facts/cds/',
        read: '2026-10-01',
    },
    timeline: {
        label: 'USD Undergraduate Admission, Admission Timeline',
        url: 'https://www.sandiego.edu/admission-and-aid/undergraduate/apply/admission-timeline.php',
        read: '2026-10-01',
    },
    decisionFaq: {
        label: 'USD Undergraduate Admission, Frequently Asked Questions',
        url: 'https://www.sandiego.edu/admission-and-aid/undergraduate/apply/decision-faq.php',
        read: '2026-10-01',
    },
    requirements: {
        label: 'USD Undergraduate Admission, Application Requirements',
        url: 'https://www.sandiego.edu/admission-and-aid/undergraduate/apply/application-requirements.php',
        read: '2026-10-01',
    },
    testFree: {
        label: 'USD Undergraduate Admission, Test-Free Information',
        url: 'https://www.sandiego.edu/admission-and-aid/undergraduate/apply/test-free.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?122436-University-of-San-Diego',
        read: '2026-10-15',
    },
    coa: {
        label: 'USD Torero Hub, Undergraduate Educational Cost (2026-27)',
        url: 'https://www.sandiego.edu/torero-hub/tuition-and-fees/undergraduate.php',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'university-of-san-diego',
    kbId: 'university_of_san_diego',
    name: 'University of San Diego',
    shortName: 'USD',
    aliases: ['USD', 'San Diego'],
    location: 'San Diego, CA',
    control: 'Private',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (USD brand manual); decoration only.
    branding: {
        primary: '#003B70',
        secondary: '#75BEE9',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_san_diego_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 17468,
        admitted: 9359,
        enrolled: 1300,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 9397, admitted: 5154 },
            { group: 'Out-of-state', applied: 7125, admitted: 3740 },
            { group: 'International', applied: 946, admitted: 465 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'USD\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'USD offers binding Early Decision and a nonbinding Early Action plan. The Common Data Set leaves its early decision counts blank and gives no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Early December', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'Mid December', binding: false },
            { plan: 'Early Decision II', deadline: 'January 5', decision: 'Late January', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'Mid March', binding: false },
        ],
        note: 'The FAFSA is due November 15 for Early Decision I and Early Action, January 5 for Early Decision II and February 1 for Regular Decision. Admitted students confirm enrollment by January 10 (Early Decision I), February 10 (Early Decision II) or May 1 (Early Action and Regular Decision).',
        source: 'timeline',
        binding: 'Early Decision I and II are binding: if admitted, you agree to enroll at USD and withdraw your other applications. You can ask to be released if the financial aid offer does not make attending financially feasible. Applicants denied Early Decision are not moved to Regular Decision.',
        bindingSource: 'decisionFaq',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'USD is test-free through fall 2027: SAT and ACT scores are not considered, and USD asks applicants not to send them. The policy covers international applicants too, who still show English proficiency.',
        source: 'testFree',
        cdsLine: 'The Common Data Set also answers no when asked whether USD uses SAT or ACT scores in admission decisions.',
        cdsWhere: 'section C8A',
    },

    currentChanges: [
        {
            topic: 'Application deadlines',
            line: 'USD says new application deadlines take effect starting with the fall 2027 entering class: November 1 for Early Action and Early Decision I, January 5 for Early Decision II and January 15 for Regular Decision.',
            source: 'requirements',
        },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    // The test-free policy is stated through fall 2027; USD has not said
    // what applies for fall 2028.
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testFree',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 64100 },
            { item: 'Fees', amount: 1099 },
            { item: 'Housing (on campus, average)', amount: 14042 },
            { item: 'Meals', amount: 8344 },
            { item: 'Personal expenses', amount: 3969 },
            { item: 'Transportation', amount: 1386 },
            { item: 'Books and supplies', amount: 1320 },
        ],
        total: 94260,
        note: 'USD\'s 2026-27 cost of living budget for on-campus students. Health insurance ($4,158) is billed unless waived and is not included.',
        source: 'coa',
        netPrice: {
            average: 29539,
            byIncome: [
                { income: '$0 to $30,000', amount: 16814 },
                { income: '$30,001 to $48,000', amount: 17381 },
                { income: '$48,001 to $75,000', amount: 21109 },
                { income: '$75,001 to $110,000', amount: 24962 },
                { income: 'Over $110,000', amount: 52698 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from USD\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
